<template>
  <!-- 🔷 External Window Mode: Blank container nur mit Content -->
  <div v-if="isExternalWindow" class="h-screen w-screen flex flex-col overflow-hidden">
    <div class="flex-1 overflow-auto">
      <RouterView name="default" />
    </div>
  </div>

  <!-- 🔲 Normal Desktop Layout (with Navigation, Header, etc) -->
  <div v-else>
    <div
      v-if="viewportStore.isMobile"
      class="fixed top-0 left-0 right-0 h-[50px] flex items-center px-4 z-[1100] shadow-md"
      style="background-color: var(--p-darkbg-500)"
    >
      <div class="flex items-center cursor-pointer" @click="viewportStore.toggleMobileSidebar()">
        <img alt="logo" src="@/assets/logo.png" width="80" />
      </div>
      <div class="flex items-center ml-auto">
        <ThemeToggle />
      </div>
    </div>

    <!-- Mobile Sidebar Overlay -->
    <Transition name="overlay-fade">
      <div
        v-if="viewportStore.isMobile && viewportStore.isMobileSidebarOpen"
        class="fixed inset-0 bg-black/50 z-[1000] backdrop-blur-sm"
        @click="viewportStore.closeMobileSidebar()"
      ></div>
    </Transition>

    <Splitter
      :class="[
        'navsplitter',
        'flex',
        'h-full',
        'w-full',
        `focused-${focusedArea}`,
        { 'mobile-layout': viewportStore.isMobile },
      ]"
      :pt="pt"
      style="height: 100vh"
      @resizeend="onNavsplitterResizeEnd"
    >
      <SplitterPanel
        :class="[
          'mainnav-panel',
          {
            'sidebar-collapsed': splitterStore.isCollapsed && !viewportStore.isMobile,
            'mobile-sidebar': viewportStore.isMobile,
            'mobile-sidebar-expanded': viewportStore.isMobile && viewportStore.isMobileSidebarOpen,
          },
        ]"
        :size="
          splitterStore.isCollapsed && !viewportStore.isMobile
            ? collapsedSizeInPercent
            : panelSizeLeftInPercent
        "
        :minSize="
          splitterStore.isCollapsed && !viewportStore.isMobile
            ? collapsedSizeInPercent
            : minSizeInPercent
        "
      >
        <Notification />
        <div
          class="flex h-full w-full"
          style="height: 100vh; background-color: var(--p-darkbg-500)"
        >
          <FocusArea areaId="mainNavigationArea" class="flex h-full w-full">
            <DesktopDrawer>
              <!-- <CurrentFocus /> -->
              <DesktopDrawerNavigation />
            </DesktopDrawer>
          </FocusArea>
        </div>
      </SplitterPanel>
      <SplitterPanel
        :size="100 - panelSizeLeftInPercent"
        :minSize="50"
        style="contentSplitterPanel"
      >
        <PageWrapper>
          <!-- 🟢 DEBUG: Show if Sub Component has its own split mode (nested split) -->
          <!-- <div
            v-if="subComponentHasOwnSplitMode"
            class="bg-green-100 border-2 border-green-500 text-green-800 p-3 mb-4 rounded font-mono text-sm"
          >
            ⚡ <strong>Nested Split View ACTIVE:</strong> Sub Component split mode würde hier die
            normale Split View überschreiben (activeKey:
            {{ contentModeStore.activeContentModeKey }}, mode:
            {{ contentModeStore.currentContentMode }})
          </div> -->

          <Splitter
            v-if="isSplitView && isDetailView"
            class="splitview"
            @resizeend="onContentsplitterResizeEnd"
          >
            <SplitterPanel
              ref="contentMainPanel"
              class="content-main-panel"
              :size="100 - contentPanelSizeRight"
            >
              <FocusArea areaId="mainArea" data-slot="main-panel">
                <!-- {{ 100 - contentPanelSizeRight }} -->
                <div
                  :class="[
                    'content-wrapper',
                    isSplitView ? 'split' : 'no-split',
                    'flex',
                    'flex-col',
                    ' h-screen',
                    'content-main-wrapper',
                  ]"
                >
                  <DesktopHeader />
                  <div>
                    <slot name="main">
                      <div
                        v-if="subComponentHasOwnSplitMode"
                        class="h-full w-full flex flex-col overflow-hidden"
                      >
                        <div class="flex-1 overflow-auto">
                          <RouterView name="default" />
                        </div>
                      </div>
                      <RouterView v-else name="main" />
                    </slot>
                  </div>
                  <div class="mt-auto">
                    <DesktopFooter />
                  </div>
                </div>
              </FocusArea>
            </SplitterPanel>
            <SplitterPanel
              ref="contentSplitPanel"
              class="h-full w-full content-split-panel"
              :minSize="1"
              :size="contentPanelSizeRight"
            >
              <FocusArea areaId="detailArea" data-slot="split-panel">
                <!-- Applied: {{ contentPanelSizeRight }} <br />
              Blurred: {{ contentSplitterStore.getBlurredRightPanelSizeForKey(datakey as string) }} <br />
              Focused: {{ contentSplitterStore.getFocusedRightPanelSizeForKey(datakey as string) }} <br /> -->
                <div
                  :class="[
                    'content-wrapper',
                    isSplitView ? 'split' : 'no-split',
                    'flex',
                    'flex-col',
                    ' h-screen',
                    'content-split-wrapper',
                  ]"
                >
                  <SlideWrapper v-if="isSubcomponent" :isVisible="isSlideVisible" direction="right">
                    <template #main>
                      <div class="splitcontent">
                        <DesktopHeader isSplitPanel />
                        <div>
                          <slot name="split">
                            <RouterView name="split" />
                          </slot>
                        </div>
                        <DesktopFooter isSplitPanel />
                      </div>
                    </template>

                    <template #secondary>
                      <div class="splitcontent">
                        <DesktopHeader isSplitPanel />
                        <div>
                          <slot name="default">
                            <RouterView name="default" />
                          </slot>
                        </div>
                        <DesktopFooter isSplitPanel />
                      </div>
                    </template>
                  </SlideWrapper>

                  <div v-else class="slide-wrapper">
                    <div class="splitcontent">
                      <DesktopHeader isSplitPanel />
                      <div>
                        <slot name="split">
                          <div
                            v-if="subComponentHasOwnSplitMode"
                            class="h-full w-full flex flex-col overflow-hidden"
                          >
                            <div class="flex-1 overflow-auto">
                              <RouterView name="default" />
                            </div>
                          </div>
                          <RouterView v-else name="split" />
                        </slot>
                      </div>
                      <DesktopFooter isSplitPanel />
                    </div>
                  </div>
                </div>
              </FocusArea>
            </SplitterPanel>
          </Splitter>
          <div v-else>
            <FocusArea areaId="contentArea">
              <div
                :class="[
                  'content-wrapper',
                  isSplitView ? 'split' : 'no-split',
                  'flex',
                  'flex-col',
                  ' h-screen',
                  'content-nosplit-wrapper',
                ]"
              >
                <DesktopHeader />
                <div>
                  <slot name="default">
                    <RouterView name="default" />
                  </slot>
                  <div
                    v-if="showSplitViewPlaceholder"
                    :class="['splitview-placeholder', placeholderClass]"
                  ></div>
                </div>
                <div class="mt-auto">
                  <DesktopFooter />
                </div>
              </div>
            </FocusArea>
          </div>
        </PageWrapper>
      </SplitterPanel>
    </Splitter>

    <DynamicDialog />

    <!-- DEBUG: Content Mode Store Status -->
    <!-- <div
      class="fixed bottom-4 right-4 bg-slate-800 text-white p-3 rounded shadow-lg text-xs font-mono z-[1200]"
      style="max-width: 400px"
    >
      <div class="font-bold mb-2 text-yellow-400">🔍 Content Mode Debug</div>
      <div class="space-y-1">
        <div>
          <span class="text-cyan-400">Active Key:</span>
          <span class="ml-1 font-semibold text-blue-400">{{ debugStoreStatus.activeKey }}</span>
        </div>
        <div>
          <span class="text-cyan-400">Active Mode:</span>
          <span class="ml-1 font-semibold text-blue-400">{{ debugStoreStatus.activeMode }}</span>
        </div>
        <div>
          <span class="text-cyan-400">Split View:</span>
          <span
            class="ml-1 font-semibold"
            :class="debugStoreStatus.isSplitViewActive ? 'text-green-400' : 'text-red-400'"
          >
            {{ debugStoreStatus.isSplitViewActive ? 'ON ✓' : 'OFF ✗' }}
          </span>
        </div>
        <div class="mt-2 pt-2 border-t border-gray-600">
          <span class="text-gray-300">Root Entity:</span>
          <span class="ml-1">{{ debugStoreStatus.rootEntity }}</span>
        </div>
        <div>
          <span class="text-gray-300">All Modes:</span>
          <div class="ml-2 mt-1 text-gray-400">
            <div v-for="(mode, key) in debugStoreStatus.allModes" :key="key" class="text-xs">
              {{ key }}:
              <span :class="mode === 'split' ? 'text-green-400' : 'text-gray-400'">{{ mode }}</span>
            </div>
          </div>
        </div>
      </div>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import DynamicDialog from 'primevue/dynamicdialog';
import type { SplitterResizeEndEvent } from 'primevue/splitter';
import { type RouteParamsGeneric, useRoute, useRouter } from 'vue-router';

import ThemeToggle from '@/components/layouts/common-components/ThemeToggle.vue';
import DesktopDrawer from '@/components/layouts/desktop/components/DesktopDrawer.vue';
import DesktopFooter from '@/components/layouts/desktop/components/DesktopFooter.vue';
import DesktopHeader from '@/components/layouts/desktop/components/DesktopHeader.vue';
import DesktopDrawerNavigation from '@/components/layouts/desktop/content/DesktopDrawerNavigation.vue';
import Notification from '@/components/notification/Notification.vue';
import { useContentModeStore } from '@/stores/contentmode';
import { useContentSplitterStore } from '@/stores/contentsplitter';
import { useFocusManager } from '@/stores/focusManager';
import { useSplitterStore } from '@/stores/navsplitter';
import { useReturnToStore } from '@/stores/returnTo';
import { useViewportStore } from '@/stores/viewport';
import { eventBus, type GotoPayload } from '@/configs/eventBus';

const contentModeStore = useContentModeStore();
const contentSplitterStore = useContentSplitterStore();
const returnToStore = useReturnToStore();
const focusManager = useFocusManager();
const viewportStore = useViewportStore();

const route = useRoute();
const router = useRouter();

const splitterStore = useSplitterStore();
const windowWidth = computed(() => viewportStore.width);
const minWidthInPixels = 200;

// ✅ External Window Check: Is this rendered in external browser window?
const isExternalWindow = computed(() => {
  return route.meta.isExternalWindow === true;
});

// ✅ Split View Status: Get from NEW contentMode store
// Now uses activeContentModeKey (can be overridden by nested ContentModeWrapper)
const currentEntity = computed(() => route.path.split('/').filter(Boolean)[0] || 'default');
const isSplitView = computed(() => {
  // Use the ACTIVE key from store (may be different from route root!)
  const activeKey = contentModeStore.activeContentModeKey;
  const mode = contentModeStore.contentModeByRoute[activeKey];
  const result = mode === 'split';
  return result;
});

// Detail view: entity/id or entity/id/action (at least 2 segments)
const isDetailView = computed(() => route.path.split('/').filter(Boolean).length >= 2);
const isSubcomponent = ref(false);
const r = ref(route);
const datakey = computed(() => r.value.params.component);
const showSplitViewPlaceholder = computed(() => {
  return !isDetailView.value;
});

// ✅ CHECK: Sub Component has its own Split Mode (nested ContentModeWrapper with split mode)
// This means the Sub Component's ContentModeWrapper would override the normal Desktop split layout
const subComponentHasOwnSplitMode = computed(() => {
  const activeKey = contentModeStore.activeContentModeKey;
  const rootEntity = currentEntity.value;
  const activeMode = contentModeStore.currentContentMode;

  const result = activeKey !== rootEntity && activeMode === 'split';
  return result;
});

// Context is now set via data-slot attributes on FocusArea components
// No setter functions needed - ContentModeWrapper reads from parent DOM

const isDetailAreaFocused = ref(false);
const contentPanelSizeRight = ref(
  contentSplitterStore.getBlurredRightPanelSizeForKey(datakey.value as string),
);
const contentPanelSizeLeft = ref(
  100 - contentSplitterStore.getBlurredRightPanelSizeForKey(datakey.value as string),
);

function calculateMinWidthVW(mainnavPanelElement: HTMLElement): string {
  const blurredRightSize = contentSplitterStore.getBlurredRightPanelSizeForKey(
    datakey.value as string,
  );
  const factorContentwidthWithoutMainNavigation =
    1 - mainnavPanelElement.clientWidth / window.innerWidth;
  const vw = (100 - blurredRightSize) * factorContentwidthWithoutMainNavigation;

  return `${vw}vw`;
}

// Method to apply panel size change
async function applyPanelShift() {
  await nextTick();

  const mainnavPanelElement = document.querySelector('.mainnav-panel') as HTMLElement;
  const mainPanelElement = document.querySelector('.content-main-panel') as HTMLElement;
  const mainPanelContentElement = document.querySelector('.content-main-wrapper') as HTMLElement;
  const splitPanelElement = document.querySelector('.content-split-panel') as HTMLElement;

  if (!mainnavPanelElement || !mainPanelElement || !mainPanelContentElement || !splitPanelElement)
    return;

  if (mainPanelElement && splitPanelElement) {
    const mainBasis = `calc(${100 - contentPanelSizeRight.value}% - 10px)`;
    const splitBasis = `calc(${contentPanelSizeRight.value}% - 10px)`;
    const mainPanelContentMinwidth = calculateMinWidthVW(mainnavPanelElement);
    calculateMinWidthVW(mainnavPanelElement);

    mainPanelElement.style.flexBasis = mainBasis;
    mainPanelContentElement.style.width = mainPanelContentMinwidth; // Set minimum width
    // mainPanelElement.style.backgroundColor = isDetailAreaFocused.value ? 'red' : 'green'; // Beispiel-Hintergrundfarbe

    splitPanelElement.style.flexBasis = splitBasis;
    // splitPanelElement.style.backgroundColor = isDetailAreaFocused.value ? 'green' : 'red'; // Beispiel-Hintergrundfarbe
  }
}

// Resize-Handling
function onContentsplitterResizeEnd(event: SplitterResizeEndEvent) {
  // Guard against undefined sizes; fall back to the currently applied right panel size
  const newRightSize = event.sizes?.[1] ?? contentPanelSizeRight.value; // Breite des rechten Panels in Prozent

  if (isDetailAreaFocused.value) {
    contentSplitterStore.setFocusedRightPanelSizeForKey(datakey.value as string, newRightSize); // Aktualisiere Fokusbreite
  } else {
    contentSplitterStore.setBlurredRightPanelSizeForKey(datakey.value as string, newRightSize); // Aktualisiere Geblurrte Breite
  }
  //  applyPanelShift();
}

window.addEventListener('resize', () => {
  viewportStore.setSize(window.innerWidth, window.innerHeight);

  // Auto-resize sidebar based on viewport
  if (viewportStore.isMobile) {
    splitterStore.setCollapsed(true);
    splitterStore.setPanelSizeLeftInPixels(splitterStore.collapsedWidth);
  } else {
    splitterStore.setCollapsed(false);
    splitterStore.setPanelSizeLeftInPixels(300);
  }
});

const minSizeInPercent = computed(() => {
  return (minWidthInPixels / windowWidth.value) * 100;
});

const collapsedSizeInPercent = computed(() => {
  return (splitterStore.collapsedWidth / windowWidth.value) * 100;
});

const panelSizeLeftInPercent = computed(() => {
  return (splitterStore.panelSizeLeftInPixels / windowWidth.value) * 100;
});

function onNavsplitterResizeEnd(event: SplitterResizeEndEvent) {
  // Use a null-safe access to sizes and fall back to the current panel percent
  const leftPercent = event.sizes?.[0] ?? panelSizeLeftInPercent.value;
  const newLeftSizeInPixels = (leftPercent / 100) * windowWidth.value;
  splitterStore.setPanelSizeLeftInPixels(newLeftSizeInPixels);
}

const pt = {};

const isSlideVisible = ref(false); // true zeigt Subcomponent, false zeigt Main Slide

const showRelatedDetail = () => {
  isSlideVisible.value = false;
  setTimeout(() => {
    isSlideVisible.value = true;
  }, 1000);
};

const closeRelatedDetail = () => {};

const handleRelatedDetailVisibility = (newParams: RouteParamsGeneric) => {
  if ((newParams.subComponentID || newParams.subComponentMode) && isSplitView.value) {
    isSubcomponent.value = true;
    showRelatedDetail();
  } else {
    isSubcomponent.value = false;
    closeRelatedDetail();
  }
};

const placeholderClass = computed(() => {
  return isSplitView.value ? 'split' : 'no-split';
});

eventBus.on('closeSubcomponent', (payload: GotoPayload) => {
  const path = typeof payload === 'string' ? payload : '';
  if (!path) return;

  if (isSplitView.value) {
    isSlideVisible.value = false;
    window.history.replaceState({}, '', path);
    setTimeout(() => void router.push(path), 1000);
  } else {
    void router.push(path);
  }
});

eventBus.on('goto', (payload: GotoPayload) => {
  const { path, returnTo, replace } =
    typeof payload === 'string' ? { path: payload, returnTo: null, replace: false } : payload;

  if (!path) return;

  // If not provided, default to current route as return target
  const from = returnTo ?? route.fullPath;
  returnToStore.set(from);

  // Push/Replace to target
  if (replace) {
    void router.replace(path);
  } else {
    void router.push(path);
  }
});

const focusedArea = ref();

watch(
  () => focusManager.currentFocusIndex,
  () => {
    focusedArea.value = focusManager.focusAreas[focusManager.currentFocusIndex];
    isDetailAreaFocused.value = focusedArea.value === 'detailArea';

    if (isDetailAreaFocused.value) {
      contentPanelSizeRight.value = contentSplitterStore.getFocusedRightPanelSizeForKey(
        datakey.value as string,
      );
      contentPanelSizeLeft.value =
        100 - contentSplitterStore.getFocusedRightPanelSizeForKey(datakey.value as string);
    } else {
      contentPanelSizeRight.value = contentSplitterStore.getBlurredRightPanelSizeForKey(
        datakey.value as string,
      );
      contentPanelSizeLeft.value =
        100 - contentSplitterStore.getBlurredRightPanelSizeForKey(datakey.value as string);
    }
    void applyPanelShift();
  },
);

watch(isDetailAreaFocused, () => {
  void applyPanelShift();
});

watch(
  () => r.value.params,
  (newParams) => {
    handleRelatedDetailVisibility(newParams);
  },
  { immediate: true, deep: true },
);

watch(isDetailAreaFocused, (isFocused) => {
  if (isFocused) {
    contentSplitterStore.setFocusedRightPanelSizeForKey(
      datakey.value as string,
      contentSplitterStore.getFocusedRightPanelSizeForKey(datakey.value as string),
    );
  } else {
    contentSplitterStore.setBlurredRightPanelSizeForKey(
      datakey.value as string,
      contentSplitterStore.getBlurredRightPanelSizeForKey(datakey.value as string),
    );
  }
});

watch(
  () => route.fullPath,
  () => {
    focusedArea.value = focusManager.focusAreas[focusManager.focusAreas.length - 1];
  },
);

onMounted(() => {
  viewportStore.setSize(window.innerWidth, window.innerHeight);
  // Set initial sidebar size based on viewport
  if (viewportStore.isMobile) {
    splitterStore.setCollapsed(true);
    splitterStore.setPanelSizeLeftInPixels(splitterStore.collapsedWidth);
  } else {
    splitterStore.setCollapsed(false);
    if (splitterStore.panelSizeLeftInPixels < 200) {
      splitterStore.setPanelSizeLeftInPixels(300);
    }
  }
  void applyPanelShift();
});

onMounted(() => {
  splitterStore.setPanelSizeLeftInPixels(splitterStore.panelSizeLeftInPixels);
  handleRelatedDetailVisibility(r.value.params);
});

// onUnmounted(() => {
//   eventBus.off('closeDialog');
// });
</script>

<style lang="css">
@config "../../../../tailwind.config.js";
@reference "tailwindcss";

.content-wrapper {
  @apply flex flex-col min-h-full h-screen overflow-y-auto;
}

.content-wrapper.content-nosplit-wrapper {
  @apply px-12;
}

.content-wrapper.content-split-wrapper {
  @apply px-0;
  /* @apply pl-10 pr-12; /* fewer padding left due to handle width */
  min-width: 30rem;
  border: 1px solid transparent;
  border-left: none;
}

.splitcontent {
  @apply pl-10 pr-12;
  background-color: var(--surface-ground);
}

.focused-detailArea .splitview .content-wrapper.content-split-wrapper {
  border: 1px solid var(--split-wrapper-border);
  border-left: none;
}

.content-wrapper.content-main-wrapper {
  @apply px-12;
  /* @apply pl-12 pr-0 -mr-2; */
  overflow-x: hidden;
}

.p-splitter.navsplitter {
  border: none;
}

.p-splitter-gutter {
  background-color: transparent;
}

.isSplit {
  min-width: 300px; /* contentSplitPanel content shall have some space when minimized */
}

/* Custom styling for the splitter */
.splitview .p-splitter-gutter {
  position: relative;
  width: 10px;
  cursor: col-resize;
  box-shadow: -10px 0 10px rgba(0, 0, 0, 0.4);
  transition: box-shadow 0.2s ease-in-out;
  z-index: 1;
  border: 1px solid transparent;
  border-right: none;
}

.splitview-placeholder {
  position: absolute;
  width: 10px;
  right: 0;
  top: 0;
  bottom: 0;
  box-shadow: -10px 0 10px rgba(0, 0, 0, 0.4);
  transition: box-shadow 0.2s ease-in-out;
  z-index: 2;
  border: 1px solid transparent;
  transition:
    transform 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
  transform: translateX(200%);
  pointer-events: none;
}

.splitview-placeholder.split {
  transform: translateX(0);
}

.focused-detailArea .splitview .p-splitter-gutter {
  border: 1px solid var(--split-wrapper-border);
  border-right: none;
  box-shadow: -10px 0 10px rgba(0, 0, 0, 0.6);
}

/* Optional: Hover effect for the gutter */
.splitview .p-splitter-gutter:hover {
  box-shadow: -10px 0 10px rgba(0, 0, 0, 0.6);
}

.content-main-panel,
.content-split-panel {
  transition:
    flex-basis 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
}

/* Transition animations */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Collapsed sidebar styles */
.mainnav-panel.sidebar-collapsed {
  transition: all 0.3s ease-in-out;
}

.mainnav-panel.sidebar-collapsed .p-splitter-gutter {
  display: none;
}

/* Mobile sidebar styles */
@media (max-width: 767px) {
  .navsplitter.mobile-layout {
    padding-top: 50px;
    height: 100vh;
  }

  .navsplitter .p-splitter-gutter {
    display: none;
  }

  .mainnav-panel.mobile-sidebar {
    position: fixed !important;
    left: 0;
    top: 50px;
    height: calc(100vh - 50px);
    width: 100%;
    z-index: 1050;
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }

  /* When expanded on mobile, slide in */
  .mainnav-panel.mobile-sidebar.mobile-sidebar-expanded {
    transform: translateX(0);
  }

  /* Content takes full width on mobile */
  .contentSplitterPanel {
    width: 100% !important;
    flex-basis: 100% !important;
  }
}
</style>
